import React, { useEffect, useState } from 'react';
import { statisticsAPI, sitesAPI } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import { 
    ShoppingCart, 
    Package, 
    Truck, 
    Building2,
    Clock,
    TrendingUp,
    Calendar
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import PriceAlertsCard from '@/components/PriceAlertsCard';
import ProductReceptionsDrawer from '@/components/ProductReceptionsDrawer';
import useDrawerRestore from '@/hooks/useDrawerRestore';
import DashboardV2 from '@/pages/DashboardV2';

const COMPANY_MATCHERS = {
    'Fuji Express': (name) => (name || '').startsWith('FE '),
    'Concept Lab': (name) => name === 'Concept Lab',
    'My Food': (name) => name === 'My Food',
    'Boucherie': (name) => name === 'Boucherie',
};

const todayStr = () => new Date().toISOString().split('T')[0];
const daysAgoStr = (days) => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString().split('T')[0];
};

export default function DashboardPage() {
    const { user } = useAuth();
    // Dashboard V2 réservé aux admins/superviseurs ; les autres gardent l'accueil actuel.
    if (['admin', 'supervisor'].includes(user?.role)) return <DashboardV2 />;
    return <LegacyDashboard />;
}

function LegacyDashboard() {
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [sites, setSites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [drawerTarget, setDrawerTarget] = useState(null); // { industrial_product_id|supplier_product_id, productName, subtitle }
    // Pareto period (defaults to last 90 days)
    const [paretoStart, setParetoStart] = useState(daysAgoStr(90));
    const [paretoEnd, setParetoEnd] = useState(todayStr());
    // Scope filter for Pareto: company (Fuji Express, etc.) or a specific site
    const [companyFilter, setCompanyFilter] = useState('all');
    const [siteFilter, setSiteFilter] = useState('all');
    useDrawerRestore(setDrawerTarget);

    // Sites autorisés pour l'utilisateur : liste unique de tous les sites accessibles.
    // Si `allowed_sites` est vide/absent, l'utilisateur voit tous les sites (admin).
    const allowedSites = React.useMemo(() => {
        if (!user?.allowed_sites || user.allowed_sites.length === 0) return sites;
        return sites.filter(s => user.allowed_sites.includes(s.id));
    }, [sites, user]);
    const isRestrictedUser = React.useMemo(() => {
        return !!user?.allowed_sites && user.allowed_sites.length > 0 && user.allowed_sites.length < sites.length;
    }, [user, sites]);

    useEffect(() => {
        (async () => {
            try {
                const resp = await sitesAPI.getAll();
                setSites(resp.data || []);
            } catch (e) {
                // silent — sites only needed for shortcut bar
            }
        })();
    }, []);

    useEffect(() => {
        loadStats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paretoStart, paretoEnd, companyFilter, siteFilter, allowedSites]);

    // Ne retourne que les sites de l'entreprise ET autorisés à l'utilisateur.
    const getSiteIdsForCompany = (companyName) => {
        const matcher = COMPANY_MATCHERS[companyName];
        if (!matcher) return [];
        return allowedSites.filter(s => matcher(s.name)).map(s => s.id);
    };

    // Scope actif transmis au drawer des receptions (miroir du filtre Pareto).
    // - Site spécifique -> [siteId]
    // - Entreprise -> tous les sites autorisés de l'entreprise
    // - "Toutes" -> tous les sites autorisés (jamais null si utilisateur restreint)
    const drawerScopeSiteIds = React.useMemo(() => {
        if (siteFilter !== 'all') return [siteFilter];
        if (companyFilter !== 'all') return getSiteIdsForCompany(companyFilter);
        if (isRestrictedUser) return allowedSites.map(s => s.id);
        return null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siteFilter, companyFilter, allowedSites, isRestrictedUser]);

    const loadStats = async () => {
        try {
            const params = { start_date: paretoStart, end_date: paretoEnd };
            // Precedence : site spécifique → entreprise → sites autorisés (si utilisateur restreint).
            if (siteFilter !== 'all') {
                params.site_id = siteFilter;
            } else if (companyFilter !== 'all') {
                const ids = getSiteIdsForCompany(companyFilter);
                if (ids.length > 0) params.site_ids = ids.join(',');
            } else if (isRestrictedUser) {
                params.site_ids = allowedSites.map(s => s.id).join(',');
            }
            const response = await statisticsAPI.get(params);
            setStats(response.data);
        } catch (error) {
            console.error('Error loading stats:', error);
        } finally {
            setLoading(false);
        }
    };

    const setParetoRange = (preset) => {
        const end = todayStr();
        let days;
        switch (preset) {
            case 'week': days = 7; break;
            case 'month': days = 30; break;
            case '90days': days = 90; break;
            case '6months': days = 180; break;
            case 'year': days = 365; break;
            default: days = 90;
        }
        setParetoStart(daysAgoStr(days));
        setParetoEnd(end);
    };

    // Format a YYYY-MM-DD to DD/MM/YYYY for the period banner.
    const fmtDate = (s) => {
        if (!s) return '';
        const [y, m, d] = s.split('-');
        return `${d}/${m}/${y}`;
    };
    const periodDays = (() => {
        if (!paretoStart || !paretoEnd) return 0;
        return Math.round((new Date(paretoEnd) - new Date(paretoStart)) / 86400000) + 1;
    })();

    const renderQty = (val, max = 2) => val.toLocaleString('fr-FR', { maximumFractionDigits: max });

    /**
     * Returns the converted equivalent for a line if the original unit is not
     * already the normalized one. Ex: 80 Sac → "= 1 600 kg".
     * Returns null when no useful conversion (already in kg/L/pcs with same value).
     */
    const lineEquivalent = (item) => {
        const u = (item.unit || '').toLowerCase().trim();
        if (item.qty_kg > 0 && u !== 'kg' && u !== 'g') {
            return `${renderQty(item.qty_kg)} kg`;
        }
        if (item.qty_l > 0 && u !== 'l' && u !== 'litre' && u !== 'cl' && u !== 'ml') {
            return `${renderQty(item.qty_l)} L`;
        }
        // Pieces: only show equivalent when capacity > 1 (i.e. the unit packages multiple pieces),
        // detected by qty_pieces being noticeably greater than total_quantity.
        if (item.qty_pieces > 0 && item.qty_pieces > (item.total_quantity || 0) + 0.01) {
            return `${renderQty(item.qty_pieces, 0)} pcs`;
        }
        return null;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
            </div>
        );
    }

    const metricCards = [
        { 
            title: 'Total Commandes', 
            value: stats?.total_orders || 0, 
            icon: ShoppingCart,
            color: 'bg-blue-500'
        },
        { 
            title: 'En attente', 
            value: stats?.pending_orders || 0, 
            icon: Clock,
            color: 'bg-amber-500'
        },
        { 
            title: 'Fournisseurs', 
            value: stats?.total_suppliers || 0, 
            icon: Truck,
            color: 'bg-emerald-500'
        },
        { 
            title: 'Produits', 
            value: stats?.total_products || 0, 
            icon: Package,
            color: 'bg-purple-500'
        },
        { 
            title: 'Sites', 
            value: stats?.total_sites || 0, 
            icon: Building2,
            color: 'bg-orange-500'
        },
    ];

    return (
        <div className="space-y-8 animate-fade-in relative min-h-full" data-testid="dashboard-page">
            <div>
                <h1 className="text-3xl font-bold text-slate-900">Tableau de bord</h1>
                <p className="text-slate-500 mt-1">Vue d'ensemble de votre activité</p>
            </div>

            {/* Metrics Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {metricCards.map((metric, index) => {
                    const Icon = metric.icon;
                    return (
                        <Card key={index} className="card-interactive relative overflow-hidden" data-testid={`metric-${metric.title.toLowerCase().replace(' ', '-')}`}>
                            <div className={`absolute top-0 left-0 w-1 h-full ${metric.color}`} />
                            <CardContent className="p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-slate-500 font-medium">{metric.title}</p>
                                        <p className="text-3xl font-bold text-slate-900 mt-1">{metric.value}</p>
                                    </div>
                                    <div className={`w-12 h-12 rounded-xl ${metric.color} bg-opacity-10 flex items-center justify-center`}>
                                        <Icon className={`w-6 h-6 ${metric.color.replace('bg-', 'text-')}`} />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            {/* Price Alerts */}
            <PriceAlertsCard defaultThreshold={3} />

            {/* Pareto scope shortcuts (Entreprise / Sites FE) */}
            <div className="flex flex-col gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg" data-testid="pareto-shortcuts">
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold text-slate-500 mr-1">Entreprise :</span>
                    {['all', ...Object.keys(COMPANY_MATCHERS)].map((company) => {
                        // Filtre les entreprises pour ne montrer que celles qui contiennent
                        // au moins un site autorisé pour l'utilisateur.
                        if (company !== 'all') {
                            const matcher = COMPANY_MATCHERS[company];
                            const hasAllowedSite = allowedSites.some(s => matcher(s.name));
                            if (!hasAllowedSite) return null;
                        }
                        const isActive = companyFilter === company;
                        const label = company === 'all' ? 'Toutes' : company;
                        return (
                            <button
                                key={company}
                                type="button"
                                onClick={() => {
                                    if (siteFilter !== 'all') setSiteFilter('all');
                                    setCompanyFilter(company);
                                }}
                                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border ${
                                    isActive
                                        ? 'bg-slate-900 text-white border-slate-900'
                                        : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                                }`}
                                data-testid={`pareto-company-shortcut-${company}`}
                            >
                                {label}
                            </button>
                        );
                    })}
                </div>
                {/* Sites FE : ne s'affiche que si au moins un site FE est autorisé */}
                {allowedSites.some(s => (s.name || '').startsWith('FE ')) && (
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold text-slate-500 mr-1 flex-shrink-0">Sites FE :</span>
                    {['Génipa', 'Cluny', 'Dillon', 'Océanis', 'Vauclin', 'Siège'].map((shortName) => {
                        const fullName = `FE ${shortName}`;
                        const site = allowedSites.find(s => s.name === fullName);
                        if (!site) return null;
                        const isActive = siteFilter === site.id;
                        return (
                            <button
                                key={shortName}
                                type="button"
                                onClick={() => {
                                    if (companyFilter !== 'all') setCompanyFilter('all');
                                    setSiteFilter(isActive ? 'all' : site.id);
                                }}
                                className={`lg:flex-1 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border text-center ${
                                    isActive
                                        ? 'bg-slate-900 text-white border-slate-900'
                                        : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                                }`}
                                data-testid={`pareto-site-shortcut-${shortName.toLowerCase()}`}
                            >
                                {shortName}
                            </button>
                        );
                    })}
                </div>
                )}
            </div>

            {/* Pareto 20/80 Receptions */}
            <Card>
                <CardHeader className="pb-3">
                    <div className="flex flex-col gap-2">
                        <CardTitle className="flex items-center gap-2 flex-wrap">
                            <Package className="w-5 h-5 text-purple-500" />
                            Produits clés (20/80 des réceptions)
                            {stats?.pareto_summary && (
                                <span className="ml-auto text-xs font-normal text-slate-500">
                                    Top {stats.pareto_summary.pareto_product_count}/{stats.pareto_summary.total_product_count} produits —
                                    <span className="font-semibold text-purple-600 ml-1">
                                        {formatCurrency(stats.pareto_summary.pareto_value)}
                                    </span>
                                    <span className="text-slate-400"> / {formatCurrency(stats.pareto_summary.total_value)}</span>
                                </span>
                            )}
                        </CardTitle>
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-purple-50 text-purple-700 text-xs font-semibold border border-purple-200" data-testid="pareto-period-banner">
                                <Calendar className="w-3.5 h-3.5" />
                                Du {fmtDate(paretoStart)} au {fmtDate(paretoEnd)}
                                <span className="font-normal text-purple-500">({periodDays} jour{periodDays > 1 ? 's' : ''})</span>
                            </span>
                            <div className="flex items-center gap-1 ml-auto flex-wrap">
                                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setParetoRange('week')} data-testid="pareto-preset-week">7j</Button>
                                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setParetoRange('month')} data-testid="pareto-preset-month">30j</Button>
                                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setParetoRange('90days')} data-testid="pareto-preset-90days">90j</Button>
                                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setParetoRange('6months')} data-testid="pareto-preset-6months">6 mois</Button>
                                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setParetoRange('year')} data-testid="pareto-preset-year">1 an</Button>
                                <div className="flex items-center gap-1 ml-1">
                                    <Label className="text-[10px] text-slate-500 sr-only">Du</Label>
                                    <Input type="date" value={paretoStart} onChange={(e) => setParetoStart(e.target.value)} className="h-7 text-xs w-[130px]" data-testid="pareto-start-input" />
                                    <span className="text-slate-400 text-xs">→</span>
                                    <Input type="date" value={paretoEnd} onChange={(e) => setParetoEnd(e.target.value)} className="h-7 text-xs w-[130px]" data-testid="pareto-end-input" />
                                </div>
                            </div>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    {stats?.pareto_products?.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="w-full data-table">
                                <thead>
                                    <tr className="table-header">
                                        <th className="text-left px-1.5 py-1">Produit</th>
                                        <th className="text-right px-1.5 py-1">Quantité</th>
                                        <th className="text-right px-1.5 py-1">Valeur</th>
                                        <th className="text-right px-1.5 py-1">% produit</th>
                                        <th className="text-right px-1.5 py-1">% cumulé</th>
                                        <th className="text-right px-1.5 py-1">Nb réceptions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(() => {
                                        // Group by industrial product
                                        const groups = {};
                                        stats.pareto_products.forEach(item => {
                                            const key = item.industrial_product_id || item.product_id;
                                            if (!groups[key]) {
                                                groups[key] = {
                                                    industrial_product_name: item.industrial_product_name,
                                                    items: [],
                                                    total_value: 0,
                                                    total_reception_count: 0,
                                                    total_qty_kg: 0,
                                                    total_qty_l: 0,
                                                    total_qty_pieces: 0,
                                                };
                                            }
                                            groups[key].items.push(item);
                                            groups[key].total_value += item.total_value || 0;
                                            groups[key].total_reception_count += item.reception_count || 0;
                                            groups[key].total_qty_kg += item.qty_kg || 0;
                                            groups[key].total_qty_l += item.qty_l || 0;
                                            groups[key].total_qty_pieces += item.qty_pieces || 0;
                                        });

                                        // Sort groups by total value desc; items inside by value desc
                                        const sortedGroups = Object.values(groups).sort((a, b) => b.total_value - a.total_value);
                                        sortedGroups.forEach(g => g.items.sort((a, b) => b.total_value - a.total_value));

                                        // Cumulated % is computed at GROUP level (one step per industrial product)
                                        const totalValue = stats.pareto_summary?.total_value || 1;
                                        let cumul = 0;
                                        const rows = [];
                                        sortedGroups.forEach((g, gIdx) => {
                                            cumul += g.total_value;
                                            const groupPct = ((g.total_value / totalValue) * 100).toFixed(1);
                                            const cumulPct = ((cumul / totalValue) * 100).toFixed(1);
                                            const multi = g.items.length > 1;

                                            if (!multi) {
                                                // Single supplier product → one flat row
                                                const it = g.items[0];
                                                rows.push(
                                                    <tr key={`g-${gIdx}`} className="table-row cursor-pointer hover:bg-purple-50/30" onClick={() => setDrawerTarget({ industrial_product_id: it.industrial_product_id, productName: g.industrial_product_name, subtitle: `${it.supplier_name || ''} · ${it.product_name}` })}>
                                                        <td className="px-1.5 py-0.5 pl-6">
                                                            <div className="font-semibold text-sm text-slate-700 leading-tight">{g.industrial_product_name}</div>
                                                            <div className="grid items-baseline leading-tight pl-4" style={{ gridTemplateColumns: '400px auto' }}>
                                                                <span className="text-sm text-slate-400">{it.product_name}</span>
                                                                <span className="text-sm text-slate-400 italic whitespace-nowrap">{it.supplier_name || ''}</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-1.5 py-0.5 text-right align-bottom">
                                                            <span className="inline-flex items-baseline gap-1.5 tabular-nums">
                                                                <span className="font-semibold text-emerald-600 text-right min-w-[72px]">{it.total_quantity.toFixed(2)}</span>
                                                                <span className="text-xs text-slate-400 font-normal text-left min-w-[68px]">{it.unit || ''}</span>
                                                                <span className="text-xs text-slate-400 font-normal italic text-left min-w-[110px]">
                                                                    {lineEquivalent(it) ? `≈ ${lineEquivalent(it)}` : ''}
                                                                </span>
                                                            </span>
                                                        </td>
                                                        <td className="px-1.5 py-0.5 text-right align-bottom">
                                                            <span className="font-medium text-slate-600">{formatCurrency(g.total_value)}</span>
                                                        </td>
                                                        <td className="px-1.5 py-0.5 text-right align-bottom">
                                                            <span className="inline-flex items-center justify-end w-14 px-1.5 py-0.5 rounded bg-slate-50 text-slate-600 text-xs font-semibold tabular-nums">
                                                                {groupPct}%
                                                            </span>
                                                        </td>
                                                        <td className="px-1.5 py-0.5 text-right align-bottom">
                                                            <span className="inline-flex items-center justify-end w-14 px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 text-xs font-bold tabular-nums">
                                                                {cumulPct}%
                                                            </span>
                                                        </td>
                                                        <td className="px-1.5 py-0.5 text-right align-bottom text-slate-600">{g.total_reception_count}</td>
                                                    </tr>
                                                );
                                            } else {
                                                // Group header row: industrial product with totals
                                                rows.push(
                                                    <tr key={`g-${gIdx}-head`} className="table-row bg-slate-50/40 cursor-pointer hover:bg-purple-50/40" onClick={() => setDrawerTarget({ industrial_product_id: g.items[0].industrial_product_id, productName: g.industrial_product_name, subtitle: `${g.items.length} fournisseurs` })}>
                                                        <td className="px-1.5 pt-0.5 pb-0 pl-6">
                                                            <div className="font-semibold text-sm text-slate-700 leading-tight">{g.industrial_product_name}</div>
                                                        </td>
                                                        <td className="px-1.5 pt-0.5 pb-0 text-right align-bottom">
                                                            <span className="inline-flex items-baseline gap-1.5 tabular-nums">
                                                                <span className="min-w-[72px]"></span>
                                                                <span className="min-w-[68px]"></span>
                                                                <span className="text-left min-w-[110px] text-xs leading-tight">
                                                                    {g.total_qty_kg > 0 && (
                                                                        <div><span className="font-bold text-emerald-700">{g.total_qty_kg.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}</span> <span className="text-slate-400 font-normal">kg</span></div>
                                                                    )}
                                                                    {g.total_qty_l > 0 && (
                                                                        <div><span className="font-bold text-emerald-700">{g.total_qty_l.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}</span> <span className="text-slate-400 font-normal">L</span></div>
                                                                    )}
                                                                    {g.total_qty_pieces > 0 && (
                                                                        <div><span className="font-bold text-emerald-700">{g.total_qty_pieces.toLocaleString('fr-FR', { maximumFractionDigits: 0 })}</span> <span className="text-slate-400 font-normal">pcs</span></div>
                                                                    )}
                                                                </span>
                                                            </span>
                                                        </td>
                                                        <td className="px-1.5 pt-0.5 pb-0 text-right align-bottom">
                                                            <span className="font-bold text-slate-700">{formatCurrency(g.total_value)}</span>
                                                        </td>
                                                        <td className="px-1.5 pt-0.5 pb-0 text-right align-bottom">
                                                            <span className="inline-flex items-center justify-end w-14 px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-xs font-bold tabular-nums">
                                                                {groupPct}%
                                                            </span>
                                                        </td>
                                                        <td className="px-1.5 pt-0.5 pb-0 text-right align-bottom">
                                                            <span className="inline-flex items-center justify-end w-14 px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 text-xs font-bold tabular-nums">
                                                                {cumulPct}%
                                                            </span>
                                                        </td>
                                                        <td className="px-1.5 pt-0.5 pb-0 text-right align-bottom text-slate-700 font-semibold">{g.total_reception_count}</td>
                                                    </tr>
                                                );
                                                // Supplier product detail lines — value/%/cumul greyed out (informational only)
                                                g.items.forEach((it, iIdx) => {
                                                    const indivPct = ((it.total_value / totalValue) * 100).toFixed(1);
                                                    rows.push(
                                                        <tr key={`g-${gIdx}-${iIdx}`} className="table-row cursor-pointer hover:bg-blue-50/40" onClick={() => setDrawerTarget({ supplier_product_id: it.supplier_product_id || it.product_id, productName: g.industrial_product_name, subtitle: `${it.supplier_name || ''} · ${it.product_name}` })}>
                                                            <td className="px-1.5 py-0 pl-6">
                                                                <div className="grid items-baseline leading-tight pl-4" style={{ gridTemplateColumns: '400px auto' }}>
                                                                    <span className="text-sm text-slate-400">{it.product_name}</span>
                                                                    <span className="text-sm text-slate-400 italic whitespace-nowrap">{it.supplier_name || ''}</span>
                                                                </div>
                                                            </td>
                                                            <td className="px-1.5 py-0 text-right align-bottom">
                                                                <span className="inline-flex items-baseline gap-1.5 tabular-nums">
                                                                    <span className="font-semibold text-emerald-600 text-right min-w-[72px]">{it.total_quantity.toFixed(2)}</span>
                                                                    <span className="text-xs text-slate-400 font-normal text-left min-w-[68px]">{it.unit || ''}</span>
                                                                    <span className="text-xs text-slate-400 font-normal italic text-left min-w-[110px]">
                                                                        {lineEquivalent(it) ? `≈ ${lineEquivalent(it)}` : ''}
                                                                    </span>
                                                                </span>
                                                            </td>
                                                            <td className="px-1.5 py-0 text-right align-bottom">
                                                                <span className="text-slate-300 text-sm">{formatCurrency(it.total_value || 0)}</span>
                                                            </td>
                                                            <td className="px-1.5 py-0 text-right align-bottom">
                                                                <span className="text-slate-300 text-xs tabular-nums">{indivPct}%</span>
                                                            </td>
                                                            <td className="px-1.5 py-0 text-right align-bottom">
                                                                <span className="text-slate-300 text-xs">—</span>
                                                            </td>
                                                            <td className="px-1.5 py-0 text-right align-bottom text-slate-400">{it.reception_count}</td>
                                                        </tr>
                                                    );
                                                });
                                            }
                                        });
                                        return rows;
                                    })()}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="py-12 text-center text-slate-400">
                            <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                            <p>Aucune réception sur les 90 derniers jours</p>
                        </div>
                    )}
                </CardContent>
            </Card>

            <ProductReceptionsDrawer
                open={!!drawerTarget}
                onClose={() => setDrawerTarget(null)}
                industrialProductId={drawerTarget?.industrial_product_id || null}
                supplierProductId={drawerTarget?.supplier_product_id || null}
                productName={drawerTarget?.productName || ''}
                subtitle={drawerTarget?.subtitle || ''}
                initialStartDate={paretoStart}
                initialEndDate={paretoEnd}
                siteIds={drawerScopeSiteIds}
            />
        </div>
    );
}
